/**
 * Supplier-attach outcome vocabulary — Lane 625 (Phase 5, screen 2).
 *
 * The sibling of `docUploadStatus.js`, for the SECOND beat.
 * `POST /api/chat/upload/candidates/{id}/attach-supplier` (Lane 618) is the
 * first and only moment a claim from a supplier's letter becomes an
 * ATTRIBUTED row in the assertions store — a row naming a real supplier that a
 * buyer may later quote back at them. So the surface that reports it has to be
 * exactly as careful as the one that reports a read.
 *
 * WHY A SEPARATE MODULE, AND WHY PURE
 * -----------------------------------
 * Same reason as `docUploadStatus.js`: the mapping is the honesty, so it lives
 * where it can be unit-tested against the endpoint's real return shapes rather
 * than inferred from a rendered blob.
 *
 * THE INVARIANT (test-pinned, both directions)
 * --------------------------------------------
 *   Nothing resolves to `ATTACHED` — the only success class — unless the
 *   server says it wrote at least one assertion AND wrote every claim it had.
 *
 * That is stricter than "HTTP 200", and it has to be, because this endpoint
 * returns **200 for outcomes that recorded nothing**:
 *
 *   status='no_assertions' + claims_available=3  ->  the supplier was
 *       attributed and all three claims FAILED to write. A 200. Rendering it
 *       as success would tell a buyer their letter is captured when the store
 *       holds nothing.
 *
 *   status='fully_processed' + created=2, available=5  ->  a PARTIAL. The
 *       terminal write lists exactly the ids that landed (helpers/notice_write.py
 *       commits per claim, so a mid-loop failure leaves earlier claims durable),
 *       so the shortfall is real and is said out loud.
 *
 *   status='not_on_file'  ->  design §1's first-class outcome. Recorded, ZERO
 *       assertions, and the row STAYS attachable. Never a name-matched guess.
 *
 * FAIL CLOSED ON THE STATUS, NOT ON THE COUNTS
 * --------------------------------------------
 * A status this build has never heard of resolves to `UNKNOWN` even when the
 * counts look like a success. The counts are only trustworthy in a shape we
 * recognise; a future status could carry them with a different meaning.
 *
 * SERVER COPY IS RENDERED VERBATIM
 * --------------------------------
 * Every typed error the helper raises carries a message the route passes
 * through as `detail` (404 not-found, 409 no-notice / already-attached). This
 * module surfaces that string; it does not paraphrase it.
 *
 * "CLAIMS" IS THE INTERNAL WORD AND NEVER THE CUSTOMER-FACING ONE (D31(a))
 * -----------------------------------------------------------------------
 * The store, the endpoint and this module's own internals all say `claims` —
 * that is correct, and `claims_available` stays the field name. But a buyer
 * reads "claim" as *disputed*, when the thing being recorded is simply what
 * the supplier's letter SAID. So every rendered string names the provenance
 * instead: "3 things SKF Usa, Inc. told us in this letter."
 *
 * The count and the noun travel together in every branch, so a partial and a
 * total failure read in the same vocabulary as a success. `test_never_says_
 * claims_to_a_customer` pins it across every branch at once — a new outcome
 * that reintroduces the word fails there rather than shipping quietly.
 *
 * NO STRING PROMISES A CONTROL THAT DOES NOT EXIST (D31(a))
 * ---------------------------------------------------------
 * `POST .../candidates/{id}/retract` is live server-side, but **there is no
 * retract affordance in the UI** (Lane 616 §7 item 2, still open). So no
 * rendered string here offers one — not as a reassurance on the success path
 * ("can be retracted if this is wrong"), and not as an instruction on the
 * partial path ("Retract and try again"). A promise pointing at a button that
 * is not on the screen is a broken promise; a vaguer substitute ("tell us and
 * we'll remove them") only trades it for a support request. When the button is
 * wired, the reassurance can come back.
 */

/**
 * Outcome classes. The card keys tone and affordances off these, never off the
 * raw status string.
 */
export const ATTACH_OUTCOME = Object.freeze({
  ATTACHING: 'attaching', // in flight
  ATTACHED: 'attached', // attributed AND every claim recorded — the only success
  PARTIAL: 'partial', // attributed, some claims recorded, some lost
  NOTHING_RECORDED: 'nothing_recorded', // attributed, ZERO recorded, claims existed
  ATTACHED_EMPTY: 'attached_empty', // attributed, and there was nothing to record
  NOT_ON_FILE: 'not_on_file', // recorded as "not a supplier we hold" — nothing attributed
  FAILED: 'failed', // the attach did not happen
  UNKNOWN: 'unknown', // a shape this build does not recognise — fail closed
});

/**
 * THE ALLOW-LIST. The only statuses whose counts this build knows how to read.
 * Sourced from `helpers/notice_write.attach_supplier`'s three return sites.
 */
export const ATTACH_KNOWN_STATUSES = Object.freeze([
  'fully_processed',
  'no_assertions',
  'not_on_file',
]);

/**
 * The subset that means a supplier was actually attributed. `not_on_file` is
 * deliberately absent — it records that we could NOT attribute it.
 */
export const ATTRIBUTED_STATUSES = Object.freeze(['fully_processed', 'no_assertions']);

function plural(n, one, many) {
  return `${n} ${n === 1 ? one : many}`;
}

/**
 * "3 things" / "1 thing" — the customer-facing count of what the letter said.
 * Named so the vocabulary decision has one home rather than five call sites.
 */
function things(n) {
  return plural(n, 'thing', 'things');
}

/** "SKF Usa, Inc. told us" — or the honest fallback when the server echoed no
 *  name. Only reachable on the belt-and-braces path; the attributed branches
 *  always carry one. */
function toldUs(supplierName) {
  return `${supplierName || 'the sender'} told us`;
}

function nonNegativeInt(value) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : null;
}

/**
 * Map a 2xx attach response to a rendered outcome.
 *
 * @param {object} body  parsed JSON from POST .../attach-supplier
 * @param {string} fallbackName  the picked supplier's name, used only when the
 *   server did not echo one (it always does on the attributed paths; this is
 *   belt-and-braces so the card never renders "undefined").
 */
export function describeAttachOutcome(body, fallbackName) {
  const status = body && typeof body.status === 'string' ? body.status : null;
  const supplierName =
    (body && typeof body.supplier_name === 'string' && body.supplier_name) ||
    fallbackName ||
    null;
  const supplierId = body && body.supplier_id != null ? body.supplier_id : null;
  const recorded = nonNegativeInt(body && body.assertions_created_count);
  const available = nonNegativeInt(body && body.claims_available);

  const base = { status, supplierId, supplierName, recorded, available };

  // Fail closed FIRST. An unrecognised status never reaches the count logic.
  if (!ATTACH_KNOWN_STATUSES.includes(status)) {
    return {
      ...base,
      outcome: ATTACH_OUTCOME.UNKNOWN,
      headline: 'Wingman could not confirm what it recorded.',
      detail:
        'The confirm finished in a state this version does not recognise. Check the ' +
        'supplier before relying on anything from this letter.',
      canRepick: true,
    };
  }

  if (status === 'not_on_file') {
    return {
      ...base,
      outcome: ATTACH_OUTCOME.NOT_ON_FILE,
      headline: 'Recorded: this letter is not from a supplier on file.',
      detail:
        'Nothing was attributed to anyone. Wingman kept the letter, so you can attach a ' +
        'supplier later once one exists.',
      canRepick: true,
    };
  }

  // From here the server says a supplier WAS attributed. What matters now is
  // whether anything actually landed.
  if (recorded === null || available === null) {
    // A recognised status with unreadable counts. We know a supplier was
    // attributed and we do NOT know what was stored — say exactly that.
    return {
      ...base,
      outcome: ATTACH_OUTCOME.UNKNOWN,
      headline: supplierName
        ? `Attached to ${supplierName}, but Wingman could not confirm what it recorded.`
        : 'Wingman could not confirm what it recorded.',
      detail:
        'The confirm came back without a usable count. Check the supplier before relying ' +
        'on anything from this letter.',
      canRepick: true,
    };
  }

  if (recorded === 0 && available > 0) {
    // A 200 that recorded nothing. The loudest honest state in this module.
    return {
      ...base,
      outcome: ATTACH_OUTCOME.NOTHING_RECORDED,
      headline: supplierName
        ? `Attached to ${supplierName}, but nothing was recorded.`
        : 'Attached, but nothing was recorded.',
      detail:
        `None of the ${things(available)} this letter told us could be written. ` +
        'Nothing from it is in Wingman. Try confirming again, or re-upload the letter.',
      canRepick: true,
    };
  }

  if (recorded === 0) {
    return {
      ...base,
      outcome: ATTACH_OUTCOME.ATTACHED_EMPTY,
      headline: supplierName
        ? `Recorded ${supplierName} as the sender.`
        : 'Recorded the sender.',
      detail:
        'The letter said nothing Wingman could verify against its text, so nothing ' +
        'else was recorded.',
      canRepick: true,
    };
  }

  if (recorded < available) {
    return {
      ...base,
      outcome: ATTACH_OUTCOME.PARTIAL,
      headline: `Recorded ${recorded} of ${available} things ${toldUs(supplierName)} in this letter.`,
      // NO recovery instruction. The obvious one — "retract and try again" —
      // names a control the UI does not have (see the module note). The counts
      // above already say exactly what is and is not in Wingman.
      detail: 'The rest could not be written.',
      canRepick: false,
    };
  }

  return {
    ...base,
    outcome: ATTACH_OUTCOME.ATTACHED,
    headline: `Recorded ${things(recorded)} ${toldUs(supplierName)} in this letter.`,
    // No detail on purpose. What stood here was the retract reassurance, and
    // the headline already carries the count and the attribution — the only
    // two facts this outcome has.
    detail: null,
    canRepick: false,
  };
}

/**
 * Map a non-2xx attach response. The route's mapping (helpers/notice_write.py's
 * typed ValueErrors -> status codes) is the source for these branches:
 *   404  candidate OR supplier not found for this company (Rule 108 — a
 *        cross-tenant id is indistinguishable from a missing one, never 403)
 *   409  the candidate carries no classified notice, OR it already has ACTIVE
 *        claims (B4's recovery loop: retract, then attach the right supplier)
 *   422  the body carried an unexpected field (Pydantic extra='forbid')
 */
export function describeAttachHttpError(httpStatus, detail) {
  const text = typeof detail === 'string' && detail ? detail : '';

  if (httpStatus === 404) {
    return {
      outcome: ATTACH_OUTCOME.FAILED,
      httpStatus,
      headline: 'Wingman could not find that document any more.',
      // The server's message distinguishes a missing candidate from a supplier
      // outside this company's pool; both are 404 on purpose, and its wording
      // is the only place that distinction survives.
      detail: text || 'It may have been removed. Upload the letter again to retry.',
      canRepick: false,
      canReupload: true,
    };
  }

  if (httpStatus === 409) {
    const alreadyAttached = /active notice claims/i.test(text);
    return {
      outcome: ATTACH_OUTCOME.FAILED,
      httpStatus,
      headline: alreadyAttached
        ? 'This letter is already attached to a supplier.'
        : 'Wingman cannot attach a supplier to this document.',
      detail:
        text ||
        // Fallback only — the route always sends its own `detail` on a 409, and
        // that string is the one rendered. Kept honest anyway: it neither says
        // "claims" nor instructs a retract the UI cannot perform.
        (alreadyAttached
          ? 'What it told us is already recorded against that supplier.'
          : 'It was not read as a supplier notice.'),
      canRepick: false,
      canReupload: false,
    };
  }

  return {
    outcome: ATTACH_OUTCOME.FAILED,
    httpStatus,
    headline: 'Could not record who sent this letter.',
    // Lane 670: this used to end "Nothing was recorded." — a claim the client
    // cannot make. `create_assertion` COMMITS each claim individually
    // (helpers/notice_write.py says so three times, and wraps the write loop in
    // a `finally` precisely because "by the time anything can go wrong some
    // claims are already DURABLE"), so the write is partial-capable. This
    // fallback is reached only when the response carried NO detail string — the
    // route's own 500 always sends one — which in practice means a proxy 502 /
    // 504 whose non-JSON body `api/noticeAttach.js` maps to `body = null`. That
    // is exactly the case where the origin kept working and may have committed
    // some claims. `describeAttachNetworkError` below already refuses this same
    // claim for this same reason; the two are now consistent.
    detail: text || `The confirm failed (HTTP ${httpStatus}), so Wingman cannot say whether it was recorded.`,
    canRepick: true,
    canReupload: false,
  };
}

/** Transport failure — no HTTP status at all, so we do not know either way. */
export function describeAttachNetworkError() {
  return {
    outcome: ATTACH_OUTCOME.FAILED,
    httpStatus: null,
    headline: 'Could not reach Wingman to record the sender.',
    // Deliberately does NOT claim nothing was recorded: a request that never
    // returns may still have been served. Saying "nothing was recorded" here
    // would be a guess about the server's state.
    detail:
      'The confirm did not get an answer, so Wingman cannot say whether it was recorded. ' +
      'Check your connection and try again.',
    canRepick: true,
    canReupload: false,
  };
}
